<template>
  <div id="app__likert__form">
    <!--   FORMULAIRE   -->
    <v-text-field
      :label="$t('components.formLikert.fields.label.name')"
      v-model="question.label"
      :error-messages="labelErrors"
      color="valid"
    />

    <v-textarea
      name="text"
      :label="$t('components.formLikert.fields.text.name')"
      v-model="question.text"
      :error-messages="textErrors"
      color="valid"
    />

    <v-select
      :label="$t('components.formLikert.fields.scale.name')"
      :items="scales"
      :error-messages="scaleErrors"
      item-text="label"
      item-value="id"
      v-model="question.scale.id"
      color="valid"
    />

    <!--   ÉCHELLE INVERSÉE   -->
    <div class="tooltip">
      <v-switch
        :label="$t('components.formLikert.fields.inverted.name')"
        v-model="question.scale.inverted"
        color="valid"
      />
      <v-tooltip right content-class="app__tooltip__ernest">
        <template v-slot:activator="{ on }">
          <v-icon v-on="on" class="app__tooltip__cursor__ernest">info</v-icon>
        </template>
        <span>{{ $t('components.formLikert.fields.inverted.tooltip') }}</span>
      </v-tooltip>
    </div>

    <div class="invertion" v-if="selectedScale">
        <v-badge left :color="getBadgeColor('scaleStart')">
          <template v-slot:badge >
            {{ scaleInverted?5:0}}
          </template>
          {{ selectedScale.minLabel }}
        </v-badge>
        <v-icon>linear_scale</v-icon>
        <v-badge right :color="getBadgeColor('scaleEnd')">
          <template v-slot:badge>
            {{ scaleInverted?0:5}}
          </template>
          {{ selectedScale.maxLabel }}
        </v-badge>
    </div>

    <!--   QUESTION ACTIVE   -->
    <div class="tooltip">
      <v-switch
        :label="$t('components.formLikert.fields.isActive.name')"
        v-model="question.isActive"
        color="valid"
      />
      <v-tooltip right content-class="app__tooltip__ernest">
        <template v-slot:activator="{ on }">
          <v-icon v-on="on" class="app__tooltip__cursor__ernest">info</v-icon>
        </template>
        <span>{{ $t('components.formLikert.fields.isActive.tooltip') }}</span>
      </v-tooltip>
    </div>

    <!--   FENÊTRE DE DIALOGUE POUR L'ARBRE DES COMPÉTENCES   -->
    <v-dialog v-model="dialog" content-class="v-dialog__ernest">
      <template slot="activator" slot-scope="{ on }">
        <v-btn v-on="on" color="other3">
          {{ $t('components.formLikert.skillButtons.add.name') }}
        </v-btn>
      </template>

      <div class="card__container__skill">
        <header>
          <v-btn small absolute right text tile color="error"
                 @click.stop="dialog=false"
          >
            <v-icon>close</v-icon>
          </v-btn>

          <v-text-field
            v-model="search"
            label="Rechercher"
            hide-details
            clearable
            clear-icon="mdi-close-circle-outline"
            color="valid"
          />
        </header>

        <div class="card__content__treeview">
          <v-treeview
            :items="repositorySave"
            :search="search"
            :open.sync="open"
          >
            <template slot="prepend" slot-scope="{ item }">
              <v-checkbox v-if="item.type === 'skill' || item.type === 'subSkill'"
                          class="ma-0 pa-0"
                          v-model="item.selected"
              />
            </template>
          </v-treeview>
        </div>

        <v-divider></v-divider>

        <footer>
          <v-btn @click="addSkill" color="valid">Enregistrer</v-btn>
          <v-btn color="cancel" @click.stop="dialog=false">Annuler</v-btn>
        </footer>
      </div>
    </v-dialog>

    <p v-if="loading" class="no__skill">
      {{ $t('components.formLikert.fields.skills.loadSkill.name') }}
    </p>

    <ul v-else-if="question.skills.length !== 0">
      <skill :get-validate="getValidate"
             v-for="skill in question.skills"
             :key="skill.id"
             :skill-init="skill"
             @remove-skill="removeSkill"
      />
    </ul>

    <p v-else class="no__skill" :class="{ 'error--text': skillError }">
      {{ $t('components.formLikert.fields.skills.noSkill.name') }}
      <small class="error--text" v-if="skillError">
        <br/>
        {{ $t('components.formLikert.fields.skills.noSkill.error') }}
      </small>
    </p>

    <!--   ENSEMBLE DE BOUTONS POUR SAUVEGARDER   -->
    <save-buttons
      @save="save"
      :is-modification="isModification"
      :just-save="justSave"
    />
  </div>
</template>

<script>
import { between, integer, maxLength, required } from 'vuelidate/lib/validators'
import { mapActions, mapGetters } from 'vuex'
import { DEFAULT_WEIGHT } from '@/store/questions'
import Skill from '@/components/questions/formLikertSkills'
import saveButtons from '@/components/questions/saveButtons'

const questionType = 'likert'

export default {
  name: 'formLikert',
  components: {
    Skill,
    saveButtons
  },
  props: {
    isClone: {
      required: false,
      type: Boolean,
      default: false
    },
    isModification: {
      required: true,
      type: Boolean
    },
    justSave: {
      required: false,
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    /**
     * @type {LikertQuestionAir}
     */
    question: {
      scale: {},
      skills: []
    },
    dialog: false,
    getValidate: false,
    /**
     * @type {RepositoryAir[]}
     */
    open: [1],
    search: null,
    loading: true
  }),
  async created () {
    if (this.isModification) {
      await this.loadQuestion({
        questionType,
        id: this.$route.params.id
      })
    } else if (this.isClone) {
      await this.cloneQuestion({
        questionType,
        id: this.$route.params.id
      })
    } else {
      this.initQuestion(questionType)
    }

    await this.loadScales()

    this.question = this.getDeepCopy()

    await this.skillsSelection(this.question.skills)

    this.loading = false
  },
  computed: {
    ...mapGetters('Questions', [
      'questionLikert',
      'scales'
    ]),
    ...mapGetters('Repositories', [
      'repositorySelected',
      'selectedSkills'
    ]),
    scaleInverted () {
      return this.question.scale.inverted
    },
    selectedScale () {
      let scale = { ...this.scales.find(s => s.id === this.question.scale.id) }
      return scale.id ? scale : null
    },
    labelErrors () {
      const errors = []
      if (!this.$v.question.$dirty) return errors
      if (!this.$v.question.label.required) errors.push('Le libellé doit être renseigné.')
      if (!this.$v.question.label.maxLength) errors.push('Le libellé doit avoir moins de 50 caractères.')
      return errors
    },
    textErrors () {
      const errors = []
      if (!this.$v.question.$dirty) return errors
      if (!this.$v.question.text.required) {
        errors.push('Le texte de la question doit être renseigné.')
      }
      return errors
    },
    scaleErrors () {
      const errors = []
      if (!this.$v.question.$dirty) return errors
      if (!this.$v.question.scale.id.required) {
        errors.push('Vous devez sélectionner une échelle de Likert.')
      }
      return errors
    },
    skillError () {
      return this.$v.question.$dirty && !this.$v.question.skills.required
    },
    repositorySave () {
      return [this.repositorySelected]
    }
  },
  validations: {
    question: {
      label: {
        required,
        maxLength: maxLength(50)
      },
      text: {
        required
      },
      scale: {
        id: {
          required
        }
      },
      isActive: {
        required
      },
      skills: {
        required,
        $each: {
          weight: {
            required,
            between: between(1, 5),
            integer
          }
        }
      }
    }
  },
  methods: {
    ...mapActions('Questions', [
      'cloneQuestion',
      'initQuestion',
      'loadQuestion',
      'loadScales'
    ]),
    ...mapActions('Repositories', [
      'skillsSelection',
      'skillDeselection',
      'skillsSelectionReset'
    ]),
    addSkill () {
      const existingSkillIds = this.question.skills.map(s => s.id)
      this.question.skills = this.selectedSkills
        .map(s => existingSkillIds.includes(s.id)
          ? this.question.skills.find(skill => skill.id === s.id)
          : {
            ...s,
            weight: DEFAULT_WEIGHT
          }
        )
      this.dialog = false
    },
    getBadgeColor (position) {
      const colorSchema = {
        true: 'valid',
        false: 'cancel'
      }
      const baseMatch = position === 'scaleStart'
      return colorSchema[this.scaleInverted ? !baseMatch : baseMatch]
    },
    getDeepCopy () {
      return {
        ...this.questionLikert,
        skills: [...this.questionLikert.skills.map(s => ({ ...s }))]
      }
    },
    removeSkill (skillId) {
      this.question.skills = this.question.skills.filter(
        skill => skillId !== skill.id
      )
      this.skillDeselection(skillId)
    },
    save (extraAction = '') {
      this.validateChilds()
      this.$v.$touch()
      const saveType = 'save' + extraAction
      if (!this.$v.$invalid) {
        this.$emit('save', {
          question: this.question,
          saveType: saveType
        })
        this.skillsSelectionReset()
        if (extraAction === 'New') {
          this.$v.$reset()
          this.initQuestion(questionType)
          this.question = this.getDeepCopy()
        }
      }
    },
    validateChilds () {
      this.getValidate = true
    }
  }
}
</script>

<style lang="scss" scoped>
  @import "../../assets/style/styleAppTooltipErnest";
  @import "../../assets/style/styleAppDialogScopedErnest";

   #app__likert__form {
     .invertion {
       padding-left: 2em;
       padding-top: 2em;

       .v-icon {
         margin-right: 1em;
         margin-left: 1em;
       }
     }

     > button {
       margin-top: 30px;
     }

     .no__skill {
       text-align: center;
       margin: 15px 0 0 0;
       font-style: italic;
     }
   }
</style>

<style lang="scss">
  @import "../../assets/style/styleAppDialogErnest";
</style>
